import React, { useMemo, useState } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Card, Dropdown, Empty, Input, Modal, Spin, Typography } from 'antd';

import { EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import type { Action, Group } from 'v2/interfaces/access-control';
import accessControlService from 'v2/services/access-control';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { Icon } from '@eduzz/ui-antd-theme';

import { AccessPolicyIcon } from './Icon';

type GroupsProps = {
  onEdit: (group: Group) => void;
};

type GroupPrivilege = {
  name: string;
  actions: Action[];
};

function getGroupPrivileges(group: Group): GroupPrivilege[] {
  return Object.values(group.privileges)
    .filter(privilege => privilege.actions.length > 0)
    .map(privilege => ({
      name: privilege.name,
      actions: privilege.actions
    }));
}

function Groups({ onEdit }: GroupsProps) {
  const { message } = App.useApp();
  const [search, setSearch] = useState('');
  const [listGroupsTrigger, setListGroupsTrigger] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState({} as Group);

  const [groups] = useObservable(() => accessControlService.getGroups(), []);
  useObservable(() => accessControlService.loadGroups(), [listGroupsTrigger]);

  const [deleteGroup] = useCallbackObservable(
    groupId => {
      setIsDeleting(true);
      return accessControlService.deleteGroup(groupId).pipe(
        tap(() => {
          message.success('Sucesso, grupo removido');
          setListGroupsTrigger(prev => !prev);
          handleCloseDeleteConfirmation();
        }),
        catchError(() => {
          message.error('Erro ao remover grupo, tente novamente.');
          return EMPTY;
        }),
        finalize(() => {
          setIsDeleting(false);
        })
      );
    },
    [message]
  );

  function handleEditOptionClick(group: Group) {
    onEdit(group);
  }

  function handleRemoveOptionClick(group: Group) {
    setCurrentGroup(group);
    setIsDeleteConfirmationOpen(true);
  }

  function handleCloseDeleteConfirmation() {
    setIsDeleteConfirmationOpen(false);
  }

  function handleDeleteGroup() {
    deleteGroup(currentGroup.id);
  }

  const filteredGroups = useMemo(
    () =>
      (groups || []).filter(group => (!search || search === '' ? group : group.name.toLowerCase().includes(search))),
    [groups, search]
  );

  const editPermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:editar');
  const deletePermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:excluir');
  const shouldShowActions = editPermission || deletePermission;

  return (
    <>
      <div className='mb-4 flex justify-end'>
        <Input
          id='search-product-input'
          className='w-full md:max-w-xs'
          value={search}
          suffix={<Icon.Funnel size={16} />}
          onChange={e => setSearch(e.target.value)}
          placeholder='Pesquise pelo nome do grupo'
          allowClear
        />
      </div>

      <Modal
        open={isDeleteConfirmationOpen}
        onCancel={handleCloseDeleteConfirmation}
        title='Remover grupo'
        footer={
          <div className='flex flex-wrap items-center justify-end gap-2'>
            <Button type='text' onClick={handleCloseDeleteConfirmation} disabled={isDeleting}>
              Cancelar
            </Button>
            <Button type='primary' danger loading={isDeleting} onClick={handleDeleteGroup}>
              Remover
            </Button>
          </div>
        }
      >
        <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) px-4 py-3'>
          <Typography.Text>
            Deseja realmente remover o grupo <Typography.Text strong>{currentGroup.name}</Typography.Text>?
          </Typography.Text>
        </div>
      </Modal>

      {groups === null ? (
        <div className='flex w-full justify-center py-8'>
          <Spin />
        </div>
      ) : filteredGroups?.length > 0 ? (
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3'>
          {filteredGroups.map(group => {
            const groupPrivileges = getGroupPrivileges(group);
            return (
              <Card key={group.id} className='h-full' classNames={{ body: 'h-full' }}>
                <div className='relative flex h-full flex-col gap-3'>
                  {shouldShowActions && group.owner && (
                    <div className='absolute top-0 right-0'>
                      <Dropdown
                        menu={{
                          items: [
                            { key: 'edit', label: 'Editar', onClick: () => handleEditOptionClick(group) },
                            { key: 'remove', label: 'Remover', onClick: () => handleRemoveOptionClick(group) }
                          ]
                        }}
                        trigger={['click']}
                      >
                        <Button
                          type='text'
                          shape='circle'
                          aria-label='Ações do grupo'
                          icon={<Icon.Ellipsis size={16} />}
                        />
                      </Dropdown>
                    </div>
                  )}
                  <Typography.Title level={5} className='m-0 pr-8'>
                    {group.name}
                  </Typography.Title>
                  <div className='grid grid-cols-2 gap-3'>
                    {groupPrivileges.map(privilege => (
                      <div key={privilege.name}>
                        <Typography.Text strong className='mb-1 block text-sm'>
                          {privilege.name}
                        </Typography.Text>
                        <ul className='m-0 flex list-none flex-wrap gap-2 p-0'>
                          {privilege.actions.map(action => (
                            <AccessPolicyIcon key={action.id} action={action} />
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Empty description='Nenhum registro encontrado' />
      )}
    </>
  );
}

export { Groups };
